import { login } from '../api/client.js';
import {
  hasLocalVerifier,
  storeLocalVerifier,
  unlockWithLocalPassword,
} from '../auth/localUnlock.js';

export type GateHooks = {
  onUnlocked: () => void;
};

/**
 * Password gate — validates against a local PBKDF2 hash when present (offline).
 * First setup on a device needs one successful online login to store that hash.
 */
export function mountGate(
  root: HTMLElement,
  hooks: GateHooks,
): { focus: () => void; setHint: (text: string) => void } {
  root.innerHTML = `
    <section class="auth-gate" aria-label="Unlock">
      <div class="auth-gate-card">
        <p class="auth-gate-brand">Terratech</p>
        <h1>Enter password</h1>
        <p class="muted" id="gate-hint">Unlock works offline. Sync waits for a connection.</p>
        <form id="gate-form" method="post" action="#" autocomplete="on">
          <!-- Helps iOS Password AutoFill associate a saved password with this app. -->
          <input
            type="text"
            name="username"
            id="gate-username"
            autocomplete="username"
            value="terratech-agent"
            tabindex="-1"
            aria-hidden="true"
            class="auth-gate-username"
          />
          <label class="field">
            <span>Password</span>
            <input
              type="password"
              id="gate-password"
              name="password"
              autocomplete="current-password"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              enterkeyhint="go"
              required
              autofocus
            />
          </label>
          <button class="submit" type="submit">Unlock</button>
          <p class="flash error" id="gate-flash" aria-live="polite" hidden></p>
        </form>
      </div>
    </section>
  `;

  const form = root.querySelector('#gate-form') as HTMLFormElement;
  const input = root.querySelector('#gate-password') as HTMLInputElement;
  const flash = root.querySelector('#gate-flash') as HTMLElement;
  const hint = root.querySelector('#gate-hint') as HTMLElement;
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  void hasLocalVerifier().then((has) => {
    if (!has) {
      hint.textContent =
        'First unlock on this phone needs a connection. After that it works offline.';
    }
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void (async () => {
      flash.hidden = true;
      flash.textContent = '';
      const password = input.value;
      if (!password) {
        flash.textContent = 'Enter your password';
        flash.hidden = false;
        return;
      }
      submit.disabled = true;
      try {
        const hasVerifier = await hasLocalVerifier();
        if (hasVerifier) {
          const ok = await unlockWithLocalPassword(password);
          if (!ok) {
            flash.textContent = 'Incorrect password';
            flash.hidden = false;
            input.select();
            return;
          }
          // Best-effort server session for sync — never required to enter the app.
          if (navigator.onLine) {
            try {
              await login(password);
            } catch (err) {
              console.warn('login for sync cookie failed; continuing offline', err);
            }
          }
          input.value = '';
          hooks.onUnlocked();
          return;
        }

        // No local verifier yet — establish via server once, then store hash.
        if (!navigator.onLine) {
          flash.textContent =
            'Need a connection the first time you unlock on this phone';
          flash.hidden = false;
          return;
        }
        await login(password);
        await storeLocalVerifier(password);
        input.value = '';
        hooks.onUnlocked();
      } catch {
        flash.textContent = 'Incorrect password';
        flash.hidden = false;
        input.select();
      } finally {
        submit.disabled = false;
      }
    })();
  });

  return {
    focus: () => input.focus(),
    setHint: (text: string) => {
      hint.textContent = text;
    },
  };
}
